import React, { useState } from 'react';
import { Flower } from 'lucide-react';
import { RadioListOneColumn } from '../ui/RadioListOneColumn';
import { PrimaryButton, SecondaryButtonOutline } from '../ui/Buttons';
import { useSticker, ShapeType } from '../../context/StickerContext';
import { useToast } from '../ui/Toast';
const SHAPE_OPTIONS: ShapeType[] = ['Circle', 'Square', 'Oval', 'Rounded Corners', 'Rectangle', 'Die Cut'];
interface CutlinePanelProps {
  showInnerCutline: boolean;
  setShowInnerCutline: (val: boolean) => void;
  showOuterCutline: boolean;
  setShowOuterCutline: (val: boolean) => void;
  activeCutline: 'inner' | 'outer';
  setActiveCutline: (val: 'inner' | 'outer') => void;
}
export function CutlinePanel({
  showInnerCutline,
  setShowInnerCutline,
  showOuterCutline,
  setShowOuterCutline,
  activeCutline,
  setActiveCutline
}: CutlinePanelProps) {
  const {
    shape,
    setShape,
    cutIndividually,
    setCutIndividually
  } = useSticker();
  const {
    addToast
  } = useToast();
  // Outer Cutline State
  const [borderStyle, setBorderStyle] = useState('White Border');
  const [borderColor, setBorderColor] = useState('#FFFFFF');
  const [borderThickness, setBorderThickness] = useState(0.125);
  const [roundCorners, setRoundCorners] = useState(true);
  const [smoothing, setSmoothing] = useState(50);
  // Inner Cutline State
  const [cutMode, setCutMode] = useState('Cut to Outline (Auto)');
  const [precision, setPrecision] = useState('Medium');
  const [keepHoles, setKeepHoles] = useState(false);
  const [removeIslands, setRemoveIslands] = useState(false);
  const [islandThreshold, setIslandThreshold] = useState(0.05);
  const [edgeCleanup, setEdgeCleanup] = useState(50);
  const handleApplyOuter = () => {
    addToast('Outer cutline updated');
  };
  const handleApplyInner = () => {
    addToast('Inner cutline updated');
  };
  return <div className="h-full flex flex-col" data-id="element-47">
      <div className="p-4 border-b border-[#D9D9D9] flex flex-col gap-4" data-id="element-48">
        <h2 className="font-semibold text-[#111111]" data-id="element-49">Cutlines</h2>

        {/* Visibility Toggles */}
        <div className="space-y-3" data-id="element-50">
          <div className="flex items-center justify-between" data-id="element-51">
            <span className="text-sm font-medium text-[#111111]" data-id="element-52">
              Show Inner Cutline
            </span>
            <button onClick={() => setShowInnerCutline(!showInnerCutline)} className={`w-8 h-4 rounded-full transition-colors relative ${showInnerCutline ? 'bg-[#008696]' : 'bg-gray-200'}`} data-id="element-53">
              <div className={`w-3 h-3 bg-white rounded-full absolute top-0.5 transition-transform ${showInnerCutline ? 'left-4.5' : 'left-0.5'}`} style={{
              transform: showInnerCutline ? 'translateX(16px)' : 'translateX(0)'
            }} data-id="element-54" />
            </button>
          </div>
          <div className="flex items-center justify-between" data-id="element-55">
            <span className="text-sm font-medium text-[#111111]" data-id="element-56">
              Show Outer Cutline
            </span>
            <button onClick={() => setShowOuterCutline(!showOuterCutline)} className={`w-8 h-4 rounded-full transition-colors relative ${showOuterCutline ? 'bg-[#008696]' : 'bg-gray-200'}`} data-id="element-57">
              <div className={`w-3 h-3 bg-white rounded-full absolute top-0.5 transition-transform ${showOuterCutline ? 'left-4.5' : 'left-0.5'}`} style={{
              transform: showOuterCutline ? 'translateX(16px)' : 'translateX(0)'
            }} data-id="element-58" />
            </button>
          </div>
        </div>

        {/* Segmented Tabs */}
        <div className="bg-gray-100 p-1 rounded-full flex mt-2" data-id="element-59">
          <button onClick={() => setActiveCutline('inner')} className={`flex-1 py-1.5 px-3 text-sm font-medium rounded-full transition-all duration-200 ${activeCutline === 'inner' ? 'bg-white text-[#111111] shadow-sm' : 'text-[#5F5F5F] hover:text-[#111111]'}`} data-id="element-60">
            Inner Cutline
          </button>
          <button onClick={() => setActiveCutline('outer')} className={`flex-1 py-1.5 px-3 text-sm font-medium rounded-full transition-all duration-200 ${activeCutline === 'outer' ? 'bg-white text-[#111111] shadow-sm' : 'text-[#5F5F5F] hover:text-[#111111]'}`} data-id="element-61">
            Outer Cutline
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-8" data-id="element-62">
        {activeCutline === 'outer' ? <div className="space-y-6" data-id="element-63">
            <div className="flex items-center justify-between" data-id="element-64">
              <h3 className="font-semibold text-[#111111]" data-id="element-65">
                Outer Cutline (Sticker Border)
              </h3>
              {!showOuterCutline && <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded border border-amber-200" data-id="element-66">
                  Hidden in preview
                </span>}
            </div>

            {/* Border Style */}
            <div className="space-y-3" data-id="element-67">
              <label className="text-xs font-semibold text-[#5F5F5F] uppercase tracking-wider" data-id="element-68">
                Border Style
              </label>
              <div className="grid grid-cols-3 gap-2" data-id="element-69">
                {['No Border', 'White Border', 'Colored Border'].map(opt => {
              const isSelected = borderStyle === opt;
              return <button key={opt} onClick={() => setBorderStyle(opt)} className={`
                        flex flex-col items-center justify-center p-2 rounded-lg border transition-all text-center h-16
                        ${isSelected ? 'border-[#008696] bg-[#EAF6F7] ring-1 ring-[#008696]' : 'border-[#D9D9D9] hover:bg-gray-50'}
                      `} data-id="element-70">
                      <span className={`text-xs font-medium leading-tight ${isSelected ? 'text-[#006D78]' : 'text-[#5F5F5F]'}`} data-id="element-71">
                        {opt}
                      </span>
                    </button>;
            })}
              </div>
              {borderStyle === 'Colored Border' && <div className="flex items-center gap-3 mt-3 p-3 bg-gray-50 rounded-lg border border-[#D9D9D9]" data-id="element-72">
                  <input type="color" value={borderColor} onChange={e => setBorderColor(e.target.value)} className="w-8 h-8 rounded cursor-pointer border-0 p-0" data-id="element-73" />
                  <input type="text" value={borderColor} onChange={e => setBorderColor(e.target.value)} className="flex-1 rounded border border-[#D9D9D9] px-3 py-1.5 text-sm outline-none focus:border-[#008696] focus:ring-1 focus:ring-[#008696]" data-id="element-74" />
                </div>}
            </div>

            {/* Border Thickness */}
            <div className="space-y-3" data-id="element-75">
              <label className="text-xs font-semibold text-[#5F5F5F] uppercase tracking-wider" data-id="element-76">
                Border Thickness
              </label>
              <p className="text-xs text-[#5F5F5F] mb-2" data-id="element-77">
                This offsets the outer cut path from the inner cut path.
              </p>
              <div className="px-2" data-id="element-78">
                <input type="range" min="0" max="0.25" step="0.0625" value={borderThickness} onChange={e => setBorderThickness(parseFloat(e.target.value))} className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#008696]" data-id="element-79" />
                <div className="flex justify-between text-[10px] text-[#5F5F5F] mt-2" data-id="element-80">
                  <span data-id="element-81">None</span>
                  <span data-id="element-82">1/16"</span>
                  <span data-id="element-83">1/8"</span>
                  <span data-id="element-84">3/16"</span>
                  <span data-id="element-85">1/4"</span>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4" data-id="element-86">
                <input type="number" min="0" step="0.01" value={borderThickness} onChange={e => setBorderThickness(parseFloat(e.target.value) || 0)} className="w-24 rounded border border-[#D9D9D9] px-3 py-1.5 text-sm outline-none focus:border-[#008696] focus:ring-1 focus:ring-[#008696]" data-id="element-87" />
                <span className="text-sm text-[#5F5F5F]" data-id="element-88">inches</span>
              </div>
            </div>

            {/* Corner Handling */}
            <div className="space-y-4" data-id="element-89">
              <label className="text-xs font-semibold text-[#5F5F5F] uppercase tracking-wider" data-id="element-90">
                Corner Handling
              </label>
              <div className="flex items-center justify-between" data-id="element-91">
                <span className="text-sm font-medium text-[#111111]" data-id="element-92">
                  Round sharp corners
                </span>
                <button onClick={() => setRoundCorners(!roundCorners)} className={`w-8 h-4 rounded-full transition-colors relative ${roundCorners ? 'bg-[#008696]' : 'bg-gray-200'}`} data-id="element-93">
                  <div className={`w-3 h-3 bg-white rounded-full absolute top-0.5 transition-transform ${roundCorners ? 'left-4.5' : 'left-0.5'}`} style={{
                transform: roundCorners ? 'translateX(16px)' : 'translateX(0)'
              }} data-id="element-94" />
                </button>
              </div>
              <div data-id="element-95">
                <div className="flex justify-between mb-1" data-id="element-96">
                  <span className="text-sm font-medium text-[#111111]" data-id="element-97">
                    Smoothing
                  </span>
                  <span className="text-xs text-[#5F5F5F]" data-id="element-98">{smoothing}%</span>
                </div>
                <input type="range" min="0" max="100" value={smoothing} onChange={e => setSmoothing(parseInt(e.target.value))} className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#008696]" data-id="element-99" />
                <div className="flex justify-between text-[10px] text-[#5F5F5F] mt-1" data-id="element-100">
                  <span data-id="element-101">Low</span>
                  <span data-id="element-102">Medium</span>
                  <span data-id="element-103">High</span>
                </div>
                <p className="text-xs text-[#5F5F5F] mt-2" data-id="element-104">
                  Higher smoothing reduces jagged edges.
                </p>
              </div>
            </div>

            {/* Cut Preview Chips */}
            <div className="space-y-3" data-id="element-105">
              <label className="text-xs font-semibold text-[#5F5F5F] uppercase tracking-wider" data-id="element-106">
                Presets
              </label>
              <div className="grid grid-cols-3 gap-2" data-id="element-107">
                {['Tight', 'Medium', 'Loose'].map(opt => <button key={opt} className="flex flex-col items-center justify-center p-3 rounded-lg border border-[#D9D9D9] hover:bg-gray-50 transition-all text-center" data-id="element-108">
                    <Flower size={20} className="mb-2 text-gray-400" data-id="element-109" />
                    <span className="text-xs font-medium text-[#5F5F5F]" data-id="element-110">
                      {opt}
                    </span>
                  </button>)}
              </div>
            </div>

            {/* Buttons */}
            <div className="pt-4 border-t border-[#D9D9D9] flex gap-3" data-id="element-111">
              <SecondaryButtonOutline className="flex-1 text-xs !py-2" data-id="element-112">
                Reset
              </SecondaryButtonOutline>
              <PrimaryButton className="flex-1 text-xs !py-2" onClick={handleApplyOuter} data-id="element-113">
                Apply Outer
              </PrimaryButton>
            </div>
          </div> : <div className="space-y-6" data-id="element-114">
            <div className="flex items-center justify-between" data-id="element-115">
              <h3 className="font-semibold text-[#111111]" data-id="element-116">
                Inner Cutline (Artwork Contour)
              </h3>
              {!showInnerCutline && <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded border border-amber-200" data-id="element-117">
                  Hidden in preview
                </span>}
            </div>

            {/* Cut Mode */}
            <div className="space-y-3" data-id="element-118">
              <label className="text-xs font-semibold text-[#5F5F5F] uppercase tracking-wider" data-id="element-119">
                Cut Mode
              </label>
              <RadioListOneColumn name="cut_mode" options={['Cut to Outline (Auto)', 'Basic Shape', 'Manual Adjust']} value={cutMode} onChange={setCutMode} data-id="element-120" />
            </div>

            {cutMode === 'Basic Shape' && <div className="space-y-3 pl-4 border-l-2 border-[#008696]" data-id="element-121">
                <label className="text-xs font-semibold text-[#5F5F5F] uppercase tracking-wider" data-id="element-122">
                  Select Shape
                </label>
                <RadioListOneColumn name="inner_shape" options={SHAPE_OPTIONS} value={shape} onChange={val => setShape(val as ShapeType)} data-id="element-123" />
              </div>}

            {cutMode === 'Manual Adjust' && <div className="p-4 border-2 border-dashed border-[#D9D9D9] rounded-lg bg-gray-50 text-center" data-id="element-124">
                <p className="text-sm text-[#5F5F5F]" data-id="element-125">
                  Manual path editing tools coming here: add points, delete
                  points, drag nodes.
                </p>
              </div>}

            {/* Precision */}
            <div className="space-y-3" data-id="element-126">
              <label className="text-xs font-semibold text-[#5F5F5F] uppercase tracking-wider" data-id="element-127">
                Precision
              </label>
              <p className="text-xs text-[#5F5F5F] mb-2" data-id="element-128">
                Tight follows more detail; Loose simplifies edges.
              </p>
              <div className="grid grid-cols-3 gap-2" data-id="element-129">
                {['Loose', 'Medium', 'Tight'].map(opt => {
              const isSelected = precision === opt;
              return <button key={opt} onClick={() => setPrecision(opt)} className={`
                        flex flex-col items-center justify-center p-3 rounded-lg border transition-all
                        ${isSelected ? 'border-[#008696] bg-[#EAF6F7] ring-1 ring-[#008696]' : 'border-[#D9D9D9] hover:bg-gray-50'}
                      `} data-id="element-130">
                      <Flower size={20} className={`mb-2 ${isSelected ? 'text-[#008696]' : 'text-gray-400'}`} data-id="element-131" />
                      <span className={`text-xs font-medium ${isSelected ? 'text-[#006D78]' : 'text-[#5F5F5F]'}`} data-id="element-132">
                        {opt}
                      </span>
                    </button>;
            })}
              </div>
            </div>

            {/* Hole Handling */}
            <div className="space-y-4" data-id="element-133">
              <label className="text-xs font-semibold text-[#5F5F5F] uppercase tracking-wider" data-id="element-134">
                Hole Handling
              </label>
              <div className="flex items-center justify-between" data-id="element-135">
                <div data-id="element-136">
                  <span className="text-sm font-medium text-[#111111] block" data-id="element-137">
                    Keep interior holes
                  </span>
                  <span className="text-xs text-[#5F5F5F]" data-id="element-138">
                    e.g. inside letters like O, A
                  </span>
                </div>
                <button onClick={() => setKeepHoles(!keepHoles)} className={`w-8 h-4 rounded-full transition-colors relative ${keepHoles ? 'bg-[#008696]' : 'bg-gray-200'}`} data-id="element-139">
                  <div className={`w-3 h-3 bg-white rounded-full absolute top-0.5 transition-transform ${keepHoles ? 'left-4.5' : 'left-0.5'}`} style={{
                transform: keepHoles ? 'translateX(16px)' : 'translateX(0)'
              }} data-id="element-140" />
                </button>
              </div>
              <div className="flex items-center justify-between" data-id="element-141">
                <span className="text-sm font-medium text-[#111111]" data-id="element-142">
                  Remove tiny islands
                </span>
                <button onClick={() => setRemoveIslands(!removeIslands)} className={`w-8 h-4 rounded-full transition-colors relative ${removeIslands ? 'bg-[#008696]' : 'bg-gray-200'}`} data-id="element-143">
                  <div className={`w-3 h-3 bg-white rounded-full absolute top-0.5 transition-transform ${removeIslands ? 'left-4.5' : 'left-0.5'}`} style={{
                transform: removeIslands ? 'translateX(16px)' : 'translateX(0)'
              }} data-id="element-144" />
                </button>
              </div>
              {removeIslands && <div className="flex items-center gap-2 pl-4 border-l-2 border-[#008696]" data-id="element-145">
                  <span className="text-sm text-[#5F5F5F]" data-id="element-146">Threshold:</span>
                  <input type="number" min="0" step="0.01" value={islandThreshold} onChange={e => setIslandThreshold(parseFloat(e.target.value) || 0)} className="w-20 rounded border border-[#D9D9D9] px-2 py-1 text-sm outline-none focus:border-[#008696] focus:ring-1 focus:ring-[#008696]" data-id="element-147" />
                  <span className="text-sm text-[#5F5F5F]" data-id="element-148">sq in</span>
                </div>}
            </div>

            {/* Edge Cleanup */}
            <div className="space-y-3" data-id="element-149">
              <label className="text-xs font-semibold text-[#5F5F5F] uppercase tracking-wider" data-id="element-150">
                Edge Cleanup
              </label>
              <div data-id="element-151">
                <div className="flex justify-between mb-1" data-id="element-152">
                  <span className="text-sm font-medium text-[#111111]" data-id="element-153">
                    Cleanup Level
                  </span>
                  <span className="text-xs text-[#5F5F5F]" data-id="element-154">{edgeCleanup}</span>
                </div>
                <input type="range" min="0" max="100" value={edgeCleanup} onChange={e => setEdgeCleanup(parseInt(e.target.value))} className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#008696]" data-id="element-155" />
                <p className="text-xs text-[#5F5F5F] mt-2" data-id="element-156">
                  Helps remove stray points / noise on cut path.
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="pt-4 border-t border-[#D9D9D9] flex gap-3" data-id="element-157">
              <SecondaryButtonOutline className="flex-1 text-xs !py-2" data-id="element-158">
                Reset
              </SecondaryButtonOutline>
              <PrimaryButton className="flex-1 text-xs !py-2" onClick={handleApplyInner} data-id="element-159">
                Apply Inner
              </PrimaryButton>
            </div>
          </div>}

        {/* Global Cut Individually Toggle */}
        <div className="pt-6 border-t border-[#D9D9D9] space-y-3" data-id="element-160">
          <label className="text-xs font-semibold text-[#5F5F5F] uppercase tracking-wider" data-id="element-161">
            Finishing
          </label>
          <div className="flex items-center justify-between" data-id="element-162">
            <div data-id="element-163">
              <span className="text-sm font-medium text-[#111111] block" data-id="element-164">
                Cut Individually
              </span>
              <span className="text-xs text-[#5F5F5F]" data-id="element-165">
                Adds a one-time $15.00 fee
              </span>
            </div>
            <button onClick={() => {
            setCutIndividually(!cutIndividually);
            addToast(!cutIndividually ? 'Cut individually enabled — $15 fee applied' : 'Cut individually disabled');
          }} className={`w-8 h-4 rounded-full transition-colors relative ${cutIndividually ? 'bg-[#008696]' : 'bg-gray-200'}`} data-id="element-166">
              <div className={`w-3 h-3 bg-white rounded-full absolute top-0.5 transition-transform ${cutIndividually ? 'left-4.5' : 'left-0.5'}`} style={{
              transform: cutIndividually ? 'translateX(16px)' : 'translateX(0)'
            }} data-id="element-167" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}